import { TASK_PRIORITY_ORDER, TASK_STATUS_ORDER, TaskPriority, TaskStatus } from '@/enums/task'
import i18n from '@/libs/i18n'
import type { SelectOption, TagItem } from '@/types/Common'
import type { ITask, ITaskUpdateRequest } from '@/types/Task'

/** docs/API.md → UI labels. */
export const taskStatusLabel = (status: string): string => {
    switch (status) {
        case TaskStatus.Todo:       return i18n.global.t('task.status_todo')
        case TaskStatus.InProgress: return i18n.global.t('task.status_in_progress')
        case TaskStatus.Review:     return i18n.global.t('task.status_review')
        case TaskStatus.Done:       return i18n.global.t('task.status_done')
        default:                    return status
    }
}

export const taskPriorityLabel = (priority: string): string => {
    switch (priority) {
        case TaskPriority.Low:    return i18n.global.t('task.priority_low')
        case TaskPriority.Normal: return i18n.global.t('task.priority_normal')
        case TaskPriority.High:   return i18n.global.t('task.priority_high')
        case TaskPriority.Urgent: return i18n.global.t('task.priority_urgent')
        default:                  return priority
    }
}

/*
 * ONE COLOUR PER STATUS, used by every drawing of a task — tag, Kanban column, Gantt bar —
 * so "in progress" is the same colour wherever it is seen: to do neutral, in progress the
 * accent, in review info, done success. An OVERDUE task is error whatever its status, which
 * is the one thing a person scanning a plan has to see first.
 */
export type TaskTone = 'neutral' | 'primary' | 'info' | 'success' | 'error'

export const statusTone = (status: string): TaskTone => {
    switch (status) {
        case TaskStatus.InProgress: return 'primary'
        case TaskStatus.Review:     return 'info'
        case TaskStatus.Done:       return 'success'
        default:                    return 'neutral'
    }
}

export const taskTone = (task: Pick<ITask, 'status' | 'overdue'>): TaskTone =>
    task.overdue && task.status !== TaskStatus.Done ? 'error' : statusTone(task.status)

export const getTaskStatusTag = (status: string): TagItem => ({ label: taskStatusLabel(status), color: statusTone(status) })

export const getTaskPriorityTag = (priority: string): TagItem => {
    switch (priority) {
        case TaskPriority.Urgent: return { label: taskPriorityLabel(priority), color: 'error' }
        case TaskPriority.High:   return { label: taskPriorityLabel(priority), color: 'warning' }
        case TaskPriority.Low:    return { label: taskPriorityLabel(priority), color: 'neutral', variant: 'outline' }
        default:                  return { label: taskPriorityLabel(priority), color: 'neutral' }
    }
}

export const taskStatusIcon = (status: string): string => {
    switch (status) {
        case TaskStatus.InProgress: return 'i-lucide-circle-dot'
        case TaskStatus.Review:     return 'i-lucide-eye'
        case TaskStatus.Done:       return 'i-lucide-circle-check'
        default:                    return 'i-lucide-circle'
    }
}

export const taskStatusOptions = (): SelectOption[] =>
    TASK_STATUS_ORDER.map((status) => ({ label: taskStatusLabel(status), value: status }))

export const taskPriorityOptions = (): SelectOption[] =>
    TASK_PRIORITY_ORDER.map((priority) => ({ label: taskPriorityLabel(priority), value: priority }))

/*
 * THE GANTT'S PAINT, per tone: the bar's body, the progress filling it, and the ring that
 * outlines it. Whole class strings, because Tailwind finds a class only where it is written
 * out in full. Semantic tokens only, so both themes get them right.
 */
export const TONE_BAR: Record<TaskTone, { body: string, fill: string, ring: string, text: string }> = {
    neutral: { body: 'bg-accented',   fill: 'bg-inverted/35', ring: 'ring-accented',   text: 'text-toned' },
    primary: { body: 'bg-primary/25', fill: 'bg-primary',     ring: 'ring-primary/60', text: 'text-primary' },
    info:    { body: 'bg-info/25',    fill: 'bg-info',        ring: 'ring-info/60',    text: 'text-info' },
    success: { body: 'bg-success/25', fill: 'bg-success',     ring: 'ring-success/60', text: 'text-success' },
    error:   { body: 'bg-error/20',   fill: 'bg-error',       ring: 'ring-error/70',   text: 'text-error' },
}

/** The diamond of a milestone, per tone. */
export const TONE_MILESTONE: Record<TaskTone, string> = {
    neutral: 'bg-inverted/60',
    primary: 'bg-primary',
    info:    'bg-info',
    success: 'bg-success',
    error:   'bg-error',
}

/*
 * The full editable set of a task, as PUT /tasks/{id} wants it, with `changes` on top — what
 * the drawer's form sends and what it compares against to tell whether anything changed. (A
 * list that only ticks a task off uses PUT /tasks/{id}/status instead.)
 */
export const taskUpdateRequest = (task: ITask, changes: Partial<ITaskUpdateRequest> = {}): ITaskUpdateRequest => ({
    title:          task.title,
    description:    task.description ?? '',
    status:         task.status,
    priority:       task.priority,
    assignee_id:    task.assignee?.id ?? null,
    start_date:     task.start_date,
    due_date:       task.due_date,
    milestone:      task.milestone,
    estimate_hours: task.estimate_hours,
    progress:       task.progress,
    ...changes,
})

/** Sorts tasks the way every list shows them: the Kanban order — status, then position. */
export const byStatusAndPosition = (a: ITask, b: ITask): number =>
    TASK_STATUS_ORDER.indexOf(a.status as TaskStatus) - TASK_STATUS_ORDER.indexOf(b.status as TaskStatus)
    || a.position - b.position
